'use client';
import { getCssValue, getSizeStyle, getColorVar } from "../utils/index.js";

// Mapping for padding properties to their CSS style equivalents
const paddingStyleMap = {
  p: 'padding',
  pt: 'paddingTop',
  pb: 'paddingBottom',
  pl: 'paddingLeft',
  pr: 'paddingRight',
  px: 'paddingInline',
  py: 'paddingBlock'
};

// Mapping for margin properties to their CSS style equivalents
const marginStyleMap = {
  m: 'margin',
  mt: 'marginTop',
  mb: 'marginBottom',
  ml: 'marginLeft',
  mr: 'marginRight',
  mx: 'marginInline',
  my: 'marginBlock'
};

// Mapping for size properties to their CSS style equivalents
const sizeStyleMap = {
  w: 'width',
  h: 'height',
  minw: 'minWidth',
  maxw: 'maxWidth',
  minh: 'minHeight',
  maxh: 'maxHeight'
};

// Derive box property keys from style mappings
const boxPropKeys = [...Object.keys(paddingStyleMap), ...Object.keys(marginStyleMap), ...Object.keys(sizeStyleMap), 'bd', 'bg', 'c', 'display', 'rounded', 'shadow'];

/**
 * Extract box properties from props
 * @param props Original props object
 * @returns Object containing only box properties
 */
export const extractBoxProps = props => {
  const boxProps = {};

  // Extract only box related properties
  boxPropKeys.forEach(key => {
    if (key in props && props[key] !== undefined) {
      boxProps[key] = props[key];
    }
  });
  return boxProps;
};

/**
 * Filter out layout properties from props
 * @param props Original props object
 * @returns New object without layout properties
 */
export const omitBoxProps = props => {
  const filteredProps = {};

  // Copy all properties except box related ones
  Object.keys(props).forEach(key => {
    if (!boxPropKeys.includes(key)) {
      filteredProps[key] = props[key];
    }
  });
  return filteredProps;
};

/**
 * Converts layout properties to CSS variables with abbreviated names
 * @param props Object containing layout properties
 * @returns Object with CSS variables
 */
export const getBoxCSSVariables = props => {
  const cssVars = {};

  // Process padding properties
  Object.keys(paddingStyleMap).forEach(propKey => {
    if (props[propKey] !== undefined) {
      cssVars[`--rs-box-${propKey}`] = getCssValue(props[propKey]);
    }
  });

  // Process margin properties
  Object.keys(marginStyleMap).forEach(propKey => {
    if (props[propKey] !== undefined) {
      cssVars[`--rs-box-${propKey}`] = getCssValue(props[propKey]);
    }
  });

  // Process size properties
  Object.keys(sizeStyleMap).forEach(propKey => {
    if (props[propKey] !== undefined) {
      cssVars[`--rs-box-${propKey}`] = getCssValue(props[propKey]);
    }
  });
  if (props.bd !== undefined) {
    cssVars['--rs-box-bd'] = getCssValue(props.bd);
  }
  if (props.display !== undefined) {
    cssVars['--rs-box-display'] = props.display;
  }
  if (props.c !== undefined) {
    cssVars['--rs-box-c'] = getColorVar(props.c);
  }
  if (props.bg !== undefined) {
    cssVars['--rs-box-bg'] = getColorVar(props.bg);
  }
  return {
    ...cssVars,
    ...getSizeStyle(props.rounded, 'box', 'rounded'),
    ...getSizeStyle(props.shadow, 'box', 'shadow')
  };
};